import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '@http/clients/account.service';
import { Account } from '@models/Account';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { AccountsSingleOverviewComponent } from '../accounts-single/accounts-single-overview/accounts-single-overview.component';

@Component({
    selector: 'app-accounts-edit',
    templateUrl: './accounts-edit.component.html',
    styleUrls: ['./accounts-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SectionHeaderComponent, AccountsSingleOverviewComponent]
})
export class AccountsEditComponent implements OnInit {
    @ViewChild('overview')
    overview?: AccountsSingleOverviewComponent;

    account: Account;

    accountId: number;

    buttonLabel?: string;

    continueButtonLabel?: string;

    title?: string;

    entitySubscription?: Subscription;

    constructor(
        private readonly awaiter: AwaiterService,
        private readonly accountService: AccountService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly modalService: ModalService,
        private readonly route: ActivatedRoute,
        private readonly router: Router
    ) {
        this.account = new Account();
        this.accountId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.buttonLabel = 'Create';
        this.continueButtonLabel = 'Continue Creation';
        this.title = 'Create New Account';
    }

    async ngOnInit(): Promise<void> {
        if (this.accountId) {
            this.buttonLabel = 'Save';
            this.continueButtonLabel = 'Continue Edition';
            this.title = 'Edit Account';

            this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
                this.account = Account.fromInterface(entityData);
            });
        }
    }

    async saveAccount(): Promise<void> {
        if (!this.overview?.validate()) return;

        await this.awaiter.awaitAction('Saving Account', async () => {
            await this.accountService.save(this.account);
            this.snackbarNotificationService.success('Account saved successfully.');
            this.router.navigate(
                this.accountId ? [`/accounts-single/${this.accountId}/${TabNames[TabNames.Overview]}`] : ['/accounts']
            );
        });
    }

    async cancel(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: this.continueButtonLabel,
            content: 'Are you sure you want to exit? Your changes will be lost',
            title: 'Cancel Save Account'
        });

        if (responseOk) {
            this.router.navigate(
                this.accountId ? [`/accounts-single/${this.accountId}/${TabNames[TabNames.Overview]}`] : ['/accounts']
            );
        }
    }
}
